import React from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Fuel, Scale, Wrench, MapPin, Clock, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STOP_META = {
  fuel: { color: '#0EA5E9', label: 'Fuel', Icon: Fuel },
  scale: { color: '#8B5CF6', label: 'Scale', Icon: Scale },
  repair: { color: '#EF4444', label: 'Repair', Icon: Wrench },
  toll: { color: '#F59E0B', label: 'Toll', Icon: MapPin },
  other: { color: '#64748B', label: 'Stop', Icon: MapPin },
};

const pinIcon = (color, letter) => L.divIcon({
  className: 'stop-pin',
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
    background:${color};border:2px solid #fff;box-shadow:0 2px 5px rgba(15,23,42,.35);
    display:flex;align-items:center;justify-content:center;">
    <span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:11px;font-family:IBM Plex Sans,sans-serif;">${letter}</span>
  </div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const FitBounds = ({ points }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 11); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LoadStopsMap = ({ stops = [], height = 240, loadId = null }) => {
  const [costs, setCosts] = React.useState(null);
  React.useEffect(() => {
    if (!loadId) return;
    axios.get(`${API}/tax/loads/${loadId}/trip-costs`, { withCredentials: true })
      .then((r) => setCosts(r.data)).catch(() => {});
  }, [loadId]);
  const ordered = [...(stops || [])];
  const withCoords = ordered.filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
  const points = withCoords.map((s) => [s.lat, s.lng]);

  if (ordered.length === 0) {
    return (
      <div data-testid="load-stops-empty" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs text-slate-500 p-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        No stops logged on this trip yet.
      </div>
    );
  }

  return (
    <div data-testid="load-stops-map" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {costs && costs.count > 0 && (
        <div data-testid="trip-cost-rollup" className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trip spend</span>
            <span className="text-base font-bold text-slate-900 flex items-center gap-1"><DollarSign className="w-4 h-4 text-[#002FA7]" />{money(costs.total)}
              {costs.per_mile != null && <span className="text-xs font-semibold text-slate-500" data-testid="trip-per-mile"> · {money(costs.per_mile)}/mi</span>}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(costs.by_type).filter(([, v]) => v > 0).map(([t, v]) => {
              const meta = STOP_META[t] || STOP_META.other;
              return <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${meta.color}1A`, color: meta.color }}>{meta.label} {money(v)}</span>;
            })}
          </div>
          {(costs.rate || costs.profit_per_mile != null) && (
            <div data-testid="trip-profit" className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              {costs.rate != null && <span className="text-slate-500">Load rate <span className="font-semibold text-slate-800">{money(costs.rate)}</span></span>}
              {costs.profit != null && <span className="text-slate-500">Profit <span className={`font-semibold ${costs.profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(costs.profit)}</span></span>}
              {costs.profit_per_mile != null && <span data-testid="trip-profit-per-mile" className="text-slate-500">Profit/mi <span className={`font-semibold ${costs.profit_per_mile >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(costs.profit_per_mile)}</span></span>}
            </div>
          )}
        </div>
      )}
      {points.length > 0 && (
        <div className="rounded-lg overflow-hidden border border-slate-200 mb-3" style={{ height }}>
          <MapContainer center={points[0]} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {withCoords.map((s, i) => {
              const meta = STOP_META[s.stop_type] || STOP_META.other;
              return (
                <Marker key={s.id || i} position={[s.lat, s.lng]} icon={pinIcon(meta.color, meta.label[0])}>
                  <Popup><strong>{meta.label}</strong>{s.note ? <><br />{s.note}</> : null}<br /><span style={{ color: '#64748B' }}>{fmtTime(s.at)}</span></Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      )}
      <div className="space-y-1.5">
        {ordered.map((s, i) => {
          const meta = STOP_META[s.stop_type] || STOP_META.other;
          const Icon = meta.Icon;
          return (
            <div key={s.id || i} data-testid={`load-stop-${i}`} className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ background: `${meta.color}1A`, color: meta.color }}><Icon className="w-3.5 h-3.5" /></span>
              <span className="font-medium text-slate-800">{meta.label}</span>
              {s.note && <span className="text-slate-500">· {s.note}</span>}
              {!(typeof s.lat === 'number') && <span className="text-[10px] text-slate-400">(no GPS)</span>}
              <span className="ml-auto text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(s.at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LoadStopsMap;
